import { buildColormapLut } from "./colormaps.js";
import { remoteFetch } from "../core/remoteFetch.js";

/**
 * Spectrogram tiles fetched from the af backend, per ANALYSIS_API_SPEC.md §2.
 *
 * Tiles are grayscale PNGs the browser decodes natively, so drawing a frame is
 * a handful of `drawImage` calls rather than any per-pixel work. Only the tiles
 * overlapping the viewport are ever fetched — typically 6 to 12 — which is what
 * makes an 8-hour asset cost the same to look at as a 30-second one.
 *
 * An asset offers several **variants** — different analysis window lengths, in
 * both overlapping and non-overlapping modes. Which one is useful depends on the
 * language and speech pattern, so it's a view-time choice, not an ingest-time
 * one. Each variant caches independently, so switching back and forth is free
 * after the first look.
 */

/** Concurrent tile fetches. Enough to fill a viewport in one round trip. */
const MAX_CONCURRENT_TILE_REQUESTS = 6;

/**
 * Resident tile cap, across all variants. Each tile holds its grayscale source
 * (~32 KB) plus a coloured bitmap (~128 KB), so this is roughly 20 MB at the
 * default 128 bins and 256 columns.
 */
const MAX_RESIDENT_TILES = 120;

function createTileCanvas(width, height) {
  if (typeof OffscreenCanvas !== "undefined") return new OffscreenCanvas(width, height);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

export class RemoteSpectrogram {
  #baseUrl;
  #spec;
  #sampleRate;
  #sampleCount;
  #inFlight = new Set();
  #queue = [];
  #failed = new Set();
  #destroyed = false;

  /** key `${variantId}:${spc}:${tile}` -> {gray, width, height, bitmap, lutVersion} */
  #tiles = new Map();

  #lut;
  #lutVersion = 0;

  constructor(manifest, manifestUrl, options = {}) {
    this.manifest = manifest;
    this.#spec = manifest.spectrogram;
    this.#sampleRate = manifest.sampleRate;
    this.#sampleCount = manifest.sampleCount;

    const documentBase = typeof document !== "undefined" ? document.baseURI : undefined;
    this.#baseUrl = new URL(manifestUrl, documentBase).toString();

    this.onChange = options.onChange || null;
    this.fetchOptions = options.fetchOptions || null;
    this.stats = { requests: 0, bytes: 0, failures: 0 };

    this.variantId =
      options.variantId && this.#findVariant(options.variantId)
        ? options.variantId
        : this.#spec.defaultVariant || this.#spec.variants[0]?.id;

    this.colormap = options.colormap || "magma";
    // Default display window, not the encoded one. Speech spectra sit far below
    // full scale — a single bin rarely exceeds about -30 dBFS — so showing the
    // whole encoded -100..0 range would render almost everything near the floor.
    this.displayFloor = options.displayFloor ?? -90;
    this.displayCeiling = options.displayCeiling ?? -20;

    this.#rebuildLut();
  }

  #findVariant(id) {
    return this.#spec.variants.find((v) => v.id === id) || null;
  }

  /** The variant currently being drawn. */
  get variant() {
    return this.#findVariant(this.variantId) || this.#spec.variants[0];
  }

  /**
   * Every analysis configuration the asset offers.
   *
   * Both `windowMs` and `fftSize` are reported: window duration is what the
   * analysis actually depends on and transfers across sample rates, while FFT
   * size is what anyone used to a DSP tool reaches for. A menu entry reads well
   * as "21 ms → 512 fft".
   */
  get variants() {
    return this.#spec.variants.map((v) => ({
      id: v.id,
      fftSize: v.fftSize,
      windowMs: v.windowMs,
      window: v.window,
      overlap: v.overlap,
      // Non-overlapping analysis pins the column rate to the window length.
      columnsPerSecond: this.#sampleRate / v.tiers[v.tiers.length - 1],
      isDefault: v.id === this.#spec.defaultVariant,
    }));
  }

  /**
   * Switch analysis variant. Tiles for the previous one stay cached, so going
   * back and forth to compare is instant after the first look at each.
   */
  setVariant(id) {
    if (!this.#findVariant(id)) {
      const available = this.#spec.variants.map((v) => v.id).join(", ");
      throw new Error(`Unknown spectrogram variant "${id}". Available: ${available}`);
    }
    if (id === this.variantId) return;

    this.variantId = id;
    this.#queue = []; // anything queued was for the old variant
    this.onChange?.();
  }

  /** Frequency at the top of the image, for axis labelling. Never assume 20 kHz. */
  get maxFrequency() {
    return this.#spec.maxFrequency;
  }

  get minFrequency() {
    return this.#spec.minFrequency;
  }

  get bins() {
    return this.#spec.bins;
  }

  /**
   * How frequency is distributed up the image — "mel", "log" or "linear".
   *
   * Anything placing a tick, cropping a band, or reading a frequency off a
   * y-coordinate needs this. Without it the only safe assumption is linear, and
   * the default is mel, which puts 4 kHz at 65% of a 24 kHz axis rather than
   * 33%.
   */
  get binScale() {
    return this.#spec.binScale || "mel";
  }

  #rebuildLut() {
    this.#lut = buildColormapLut(this.colormap, {
      dbFloor: this.#spec.dbFloor,
      dbCeiling: this.#spec.dbCeiling,
      displayFloor: this.displayFloor,
      displayCeiling: this.displayCeiling,
    });
    this.#lutVersion++;
  }

  /**
   * Change the colour ramp or the displayed dB window.
   *
   * Recolours from the cached grayscale, so nothing is refetched.
   */
  setAppearance({ colormap, displayFloor, displayCeiling } = {}) {
    if (colormap !== undefined) this.colormap = colormap;
    if (displayFloor !== undefined) this.displayFloor = displayFloor;
    if (displayCeiling !== undefined) this.displayCeiling = displayCeiling;

    this.#rebuildLut();

    for (const tile of this.#tiles.values()) {
      if (tile.bitmap) {
        tile.bitmap.close?.();
        tile.bitmap = null;
      }
      tile.lutVersion = -1;
    }

    this.onChange?.();
  }

  /**
   * Tier for a zoom level: the coarsest that still gives at least one column per
   * pixel, falling back to the finest the variant has.
   *
   * For a non-overlapping variant the finest tier is the window length itself,
   * so zooming past `sampleRate / fftSize` columns per second stretches columns
   * rather than adding detail — that is the trade being made by choosing a long
   * window, not a shortcoming.
   */
  selectTier(pixelsPerSecond) {
    const tiers = this.variant.tiers; // coarsest first
    const samplesPerPixel = this.#sampleRate / pixelsPerSecond;

    let chosen = tiers[tiers.length - 1];
    for (const samplesPerColumn of tiers) {
      if (samplesPerColumn <= samplesPerPixel) {
        chosen = samplesPerColumn;
        break;
      }
    }
    return chosen;
  }

  tileCount(samplesPerColumn) {
    const columns = Math.ceil(this.#sampleCount / samplesPerColumn);
    return Math.ceil(columns / this.#spec.tileColumns);
  }

  /**
   * Tiles to draw for a time range, and a request for any that are missing.
   *
   * Unlike the waveform there's no coarser-tier fallback: a partly-drawn
   * spectrogram reads fine, whereas a stretched coarse tier butted against a
   * fine one would show a visible seam in both time and frequency resolution.
   *
   * @param {number} startTime - track-local seconds
   * @param {number} endTime - track-local seconds
   * @returns {{samplesPerColumn: number, tiles: Array<{bitmap, startTime, endTime}>}}
   */
  getTiles(startTime, endTime, pixelsPerSecond) {
    const samplesPerColumn = this.selectTier(pixelsPerSecond);
    const { tileColumns } = this.#spec;
    const samplesPerTile = tileColumns * samplesPerColumn;
    const total = this.tileCount(samplesPerColumn);

    const firstTile = Math.max(0, Math.floor((startTime * this.#sampleRate) / samplesPerTile));
    const lastTile = Math.min(total - 1, Math.floor((endTime * this.#sampleRate) / samplesPerTile));

    const tiles = [];
    const missing = [];
    const variantId = this.variantId;

    for (let index = firstTile; index <= lastTile; index++) {
      const key = `${variantId}:${samplesPerColumn}:${index}`;
      const tile = this.#tiles.get(key);

      if (!tile) {
        if (!this.#inFlight.has(key) && !this.#failed.has(key)) {
          missing.push({ variantId, samplesPerColumn, index });
        }
        continue;
      }

      // Touch for LRU: recently drawn tiles are the ones worth keeping.
      this.#tiles.delete(key);
      this.#tiles.set(key, tile);

      if (tile.lutVersion !== this.#lutVersion) this.#colorize(tile);

      // Column i covers samples [i × spc, (i+1) × spc), so a tile spans exactly
      // from its first column's start to its last column's end.
      const firstSample = index * samplesPerTile;
      tiles.push({
        bitmap: tile.bitmap,
        startTime: firstSample / this.#sampleRate,
        endTime: (firstSample + tile.width * samplesPerColumn) / this.#sampleRate,
      });
    }

    if (missing.length > 0) {
      this.#queue = missing;
      this.#pump();
    }

    return { samplesPerColumn, tiles };
  }

  #url(variantId, samplesPerColumn, tile) {
    const url = new URL("spectrogram", this.#baseUrl);
    url.searchParams.set("variant", variantId);
    url.searchParams.set("spc", String(samplesPerColumn));
    url.searchParams.set("tile", String(tile));
    return url.toString();
  }

  async #fetchTile(variantId, samplesPerColumn, index) {
    const response = await remoteFetch(
      this.#url(variantId, samplesPerColumn, index),
      this.fetchOptions
    );
    if (!response.ok) {
      throw new Error(`tile ${variantId}/${samplesPerColumn}/${index} failed: ${response.status}`);
    }

    const blob = await response.blob();
    this.stats.requests++;
    this.stats.bytes += blob.size;

    const decoded = await createImageBitmap(blob);
    const { width, height } = decoded;

    // Pull the grayscale out once and keep it. Recolouring later then costs no
    // network and no PNG decode.
    const canvas = createTileCanvas(width, height);
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(decoded, 0, 0);
    decoded.close?.();

    const { data } = ctx.getImageData(0, 0, width, height);
    const gray = new Uint8Array(width * height);
    for (let i = 0; i < gray.length; i++) gray[i] = data[i * 4]; // R == G == B

    return { gray, width, height, bitmap: null, lutVersion: -1 };
  }

  /** Apply the current LUT to a tile's grayscale, producing a drawable bitmap. */
  #colorize(tile) {
    const { gray, width, height } = tile;
    const canvas = createTileCanvas(width, height);
    const ctx = canvas.getContext("2d");
    const image = ctx.createImageData(width, height);
    const out = image.data;
    const lut = this.#lut;

    for (let i = 0; i < gray.length; i++) {
      const at = gray[i] * 3;
      out[i * 4] = lut[at];
      out[i * 4 + 1] = lut[at + 1];
      out[i * 4 + 2] = lut[at + 2];
      out[i * 4 + 3] = 255;
    }

    ctx.putImageData(image, 0, 0);
    tile.bitmap = canvas;
    tile.lutVersion = this.#lutVersion;
  }

  #pump() {
    while (this.#inFlight.size < MAX_CONCURRENT_TILE_REQUESTS && this.#queue.length > 0) {
      const { variantId, samplesPerColumn, index } = this.#queue.shift();
      const key = `${variantId}:${samplesPerColumn}:${index}`;
      if (this.#inFlight.has(key) || this.#tiles.has(key)) continue;

      this.#inFlight.add(key);

      this.#fetchTile(variantId, samplesPerColumn, index)
        .then((tile) => {
          if (this.#destroyed) return;
          this.#tiles.set(key, tile);
          this.#evict();
          this.onChange?.();
        })
        .catch(() => {
          this.stats.failures++;
          this.#failed.add(key);
        })
        .finally(() => {
          this.#inFlight.delete(key);
          if (!this.#destroyed) this.#pump();
        });
    }
  }

  /** Drop least-recently-drawn tiles. Map iteration order is insertion order. */
  #evict() {
    while (this.#tiles.size > MAX_RESIDENT_TILES) {
      const oldest = this.#tiles.keys().next().value;
      this.#tiles.get(oldest)?.bitmap?.close?.();
      this.#tiles.delete(oldest);
    }
  }

  get isFetching() {
    return this.#inFlight.size > 0;
  }

  get residentTiles() {
    return this.#tiles.size;
  }

  destroy() {
    this.#destroyed = true;
    this.#queue = [];
    for (const tile of this.#tiles.values()) tile.bitmap?.close?.();
    this.#tiles.clear();
    this.onChange = null;
  }
}
